
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { Grid } from "@mui/material";

import { ProjectInfo } from "../../../components/dashboard/projects/project-info";
import PMLinkCard from "../../../components/utils/PMLinkCard";
import {
  CameraIcon,
  ArrowPathRoundedSquareIcon,
  ExclamationCircleIcon,
  FolderIcon,
  PencilIcon,
  PaperClipIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  TableCellsIcon,
  Cog6ToothIcon,
  PrinterIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  TruckIcon,
  ChartBarIcon,
  CogIcon,
  ChevronDownIcon,
  GlobeAltIcon,
  ClipboardDocumentCheckIcon,
  ListBulletIcon,
} from "@heroicons/react/24/outline";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ItemStatus,
  mapErrors,
  updateUrlWithException,
} from "../../../components/Utils";
import { fetchProject } from "../../../api/project";
import { Notes } from "../../../components/dashboard/notes/notes";
import { HistoriesModalButton } from "../../../components/dashboard/histories/modal-button";
import { ReportsStackList } from "../../../components/dashboard/service/reports/reports-stack-list";
import { ServiceAgreementsStackList } from "../../../components/dashboard/service/agreements/agreements-stack-list";
import { TWclassNames } from "../../../components/utils/Div";
import { FoldersStackList } from "../../../components/dashboard/projects/folders/stack-list";
import { DocumentUploadSection } from "../../../components/dashboard/Media/DocumentUploadSection";
import { FolderCreateDialog } from "../../../components/dashboard/projects/folders/folder-create-dialog";
import { DeviationStacklist } from "../../../components/dashboard/quality/deviations/deviationStacklist";
import { ChecklistStacklist } from "../../../components/dashboard/quality/checklists/checklistStacklist";
import { ChecklistTypeSelector } from "../../../components/dashboard/quality/checklists/checklistTypeSelector";
import { DrawingsStackList } from "../../../components/dashboard/drawings/drawings-stack-list";
import { FieldsSection } from "../../../components/dashboard/fields/section";
import ServiceReportEditModal from "../../../components/dashboard/service/reports/service-report-edit-modal";
import { LabelPrintDialog } from "../../../components/utils/labelPrintDialog";
import { PMTabs } from "../../../components/utils/PMTabs";
import {
  useUserHasAccess,
  useProjectPermissions,
  useServiceReportsAccess,
  useServiceAgreementsAccess,
  useWorkOrdersAccess,
  useQualityDeviationsAccess,
  useProjectServiceAgreementsAccess,
  useProjectServiceReportsAccess,
  useProjectWorkOrdersAccess,
  useProjectQualityDeviationsAccess,
  useProjectQualityChecklistsAccess,
  useCompanyHasModule,
  useProjectAccountingAccess,
} from "../../../routes/roleOnly";
import { WorkOrderStacklist } from "../../../components/dashboard/workOrders/work-order-stacklist";

// Import new project management components
import { TimeRegistrationTab } from "../../../components/dashboard/projects/tabs/time-registration-tab";
import { ItemListTab } from "../../../components/dashboard/projects/tabs/item-list-tab";
import { InvoicesTab } from "../../../components/dashboard/projects/tabs/invoices-tab";
import { SupplierInvoicesTab } from "../../../components/dashboard/projects/tabs/supplier-invoices-tab";
import { BillingPlanTab } from "../../../components/dashboard/projects/tabs/billing-plan-tab";
import { ProjectSettingsTab } from "../../../components/dashboard/projects/tabs/project-settings-tab";
import { ProjectSummaryTab } from "../../../components/dashboard/projects/tabs/project-summary-tab";
import { WebsiteTab } from "../../../components/dashboard/projects/tabs/website-tab";
import { ProjectActivitiesTab } from "../../../components/dashboard/projects/tabs/project-activities-tab";
import { DocumentUploadSectionV2 } from "../../../components/dashboard/Media/DocumentUploadSectionV2";
import { DocumentUploadSectionV5, DocumentUploadSectionV6 } from "../../../components/dashboard/Media/DocumentUploadSectionV6";

export const Project = (props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUserId = useSelector((state) => state.session.currentUser?.id);

  const [itemState, setItemState] = useState({
    isLoading: true,
    error: false,
    errors: [],
    project: {},
  });

  const { project } = itemState;

  const projectPermissions = useProjectPermissions(project);
  const canProjectAccounting = useProjectAccountingAccess();
  const canEditAccountingUi =
    canProjectAccounting && (projectPermissions.canEdit || projectPermissions.isAdmin);

  const serviceAgreementAccess = useProjectServiceAgreementsAccess(project);
  const serviceReportAccess = useProjectServiceReportsAccess(project);
  const workOrderAccess = useProjectWorkOrdersAccess(project);
  const qualityDeviationAccess = useProjectQualityDeviationsAccess(project);
  const qualityChecklistAccess = useProjectQualityChecklistsAccess(project);
  const websiteModuleActive = useCompanyHasModule("website");
  const drawingsModuleActive = useCompanyHasModule("drawings");
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [folderCreateDialogOpen, setFolderCreateDialogOpen] = useState(false);
  const [checklistTypeSelectorOpen, setChecklistTypeSelectorOpen] =
    useState(false);

  const ex = projectPermissions.isExecutorParticipant;

  const rightSideActions = [
    {
      label: "Nytt Avvik",
      description: "Opprett et nytt avvik for dette prosjektet",
      path: "/dashboard/hse-ks/deviations/new",
      action: () =>
        navigate("/dashboard/hse-ks/deviations/new", {
          state: {
            project: project,
            customer: project?.customer,
          },
        }),
      icon: ExclamationCircleIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !ex && qualityDeviationAccess.canCreate,
    },
    {
      label: t("Create Checklist"),
      description: t("Create a new checklist for this project"),
      path: "",
      action: () => setChecklistTypeSelectorOpen(true),
      icon: ClipboardDocumentCheckIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !ex && qualityChecklistAccess.canCreate,
    },
    {
      label: "Service Report",
      description: "Create a service report",
      path: "",
      action: () => setReportDialogOpen(true),
      icon: Cog6ToothIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !ex && serviceReportAccess.canCreate,
    },
    {
      label: "Arbeidsordre",
      description: "Opprett en ny arbeidsordre",
      path: "/dashboard/work_orders/new",
      action: () =>
        navigate("/dashboard/work_orders/new", {
          state: {
            project: project,
            customer: project?.customer,
            contact: project?.contact,
            delivery_address: project?.delivery_address,
          },
        }),
      icon: Cog6ToothIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: !ex && workOrderAccess.canCreate,
    },
    {
      label: "Ny Mappe",
      description: "Opprett en ny mappe for dette prosjektet",
      path: "",
      action: () => setFolderCreateDialogOpen(true),
      icon: FolderIcon,
      iconForeground: "text-teal-700",
      iconBackground: "bg-teal-50",
      active: projectPermissions.canManageFolders,
    },
  ];

  const getProject = async () => {
    if (!id) return;
    
    return fetchProject(id)
      .then((res) => {
        if (res.data.id) {
          setItemState((prevState) => ({
            ...prevState,
            isLoading: false,
            project: res.data,
          }));
        }
      })
      .catch((err) => {
        let errors = null;
        if (err?.data?.errors) {
          errors = mapErrors(err.data.errors);
        } else {
          errors = ["Something went wrong"];
        }
        setItemState((prevState) => ({
          ...prevState,
          isLoading: false,
          project: {},
          error: true,
          errors: errors,
        }));
      });
  };

  useEffect(() => {
    if (!id) return;

    // Reset state when id changes
    setItemState((prevState) => ({
      ...prevState,
      isLoading: true,
      error: false,
      errors: [],
      project: {},
    }));

    // Fetch project
    getProject();
  }, [id]);

  const handleOnRefresh = (data) => {
    if (data?.id) {
      setItemState((prevState) => ({
        ...prevState,
        project: data,
      }));
    } else {
      getProject();
    }
  };

  const handleFolderCreated = (newFolder) => {
    // Refresh the project data or folders list
    console.log("New folder created:", newFolder);
    // You might want to refresh the folders list here
  };

  const handleFolderChange = (folder) => {
    setSelectedFolder(folder);
  };

  const { isLoading, error, errors } = itemState;

  const item = project;

  const [labelPrintDialogOpen, setLabelPrintDialogOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);

  const headerMeta = [
    {
      component: <ItemStatus item={project} />,
      label: "Status",
      description: "",
      path: null,
      action: null,
      icon: null,
      textColor: "text-teal-700",
      hidden: false,
    },
  ];

  const headerActions = [
    {
      component: (
        <HistoriesModalButton module={"project"} module_param={project?.id} />
      ),
      label: "History",
      description: "",
      icon: ArrowPathRoundedSquareIcon,
      textColor: "text-teal-700",
      bgColor: "bg-white",
      hidden: false,
      menu: false,
    },
    {
      component: null,
      label: "Print Label",
      description: "",
      path: null,
      action: () => setLabelPrintDialogOpen(true),
      icon: PrinterIcon,
      textColor: "text-black",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
      active: projectPermissions.can,
    },
  ];



  const tabs = [
    {
      name: "Detaljer",
      component: <ProjectInfo item={item} onRefresh={handleOnRefresh} />,
      icon: PencilIcon,
      active: projectPermissions.canRead,
    },

    {
      name: t("project.activities.tab_title"),
      component: (
        <ProjectActivitiesTab
          project={item}
          timeRegistrationTabIndex={10}
          workOrdersTabIndex={3}
        />
      ),
      icon: ListBulletIcon,
      active: projectPermissions.canRead,
    },

    {
      name: "Fields (DEV)",
      component: <FieldsSection module={"project"} moduleParam={item?.id} />,
      icon: TableCellsIcon,
      // Active false if production
      active: process.env.NODE_ENV !== "production",
    },

    {
      name: "Arbeidsordre",
      icon: WrenchScrewdriverIcon,
      active:
        workOrderAccess.canRead && projectPermissions.canCreateWorkOrders,
      component: (
        <WorkOrderStacklist
          module_param={item.id}
          module="project"
          fields="id, displayTitle, number, status, order_date, due_date, customer, project, responsible_participant, responsible_executor_participant, events, description, created_at"
          per_page="10"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },

    {
      name: "Reports",
      icon: WrenchScrewdriverIcon,
      active:
        serviceReportAccess.canRead && projectPermissions.canCreateReports,
      component: (
        <ReportsStackList
          module_param={item.id}
          module="project"
          project={item}
          fields="id, displayTitle, report_number, status, executing_employee, delivery_address, customer, report_type, appointment, description"
          per_page="10"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Folders",
      icon: FolderIcon,
      active: projectPermissions.canReadFolders,
      component: (
        <FoldersStackList
          project={item}
          // filters={[
          //   {
          //     name: "project",
          //     value: item.id,
          //   },
          // ]}
        />
      ),
    },
    {
      name: "Tegninger",
      icon: DocumentTextIcon,
      active:
        drawingsModuleActive &&
        projectPermissions.canRead &&
        !projectPermissions.isExecutorParticipant,
      component: (
        <DrawingsStackList
          project={item}
          filters={[
            {
              name: "project_id",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Agreements",
      icon: PaperClipIcon,
      active:
        serviceAgreementAccess.canRead &&
        projectPermissions.canCreateServiceAgreements,
      component: (
        <ServiceAgreementsStackList
          module_param={item.id}
          module="project"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Sjekklister",
      icon: CheckCircleIcon,
      active:
        qualityChecklistAccess.canRead && projectPermissions.canEdit,
      component: (
        <ChecklistStacklist
          module_param={item.id}
          module="project"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },
    {
      name: "Avvik",
      icon: PaperClipIcon,
      active:
        qualityDeviationAccess.canRead && projectPermissions.canEdit,
      component: (
        <DeviationStacklist
          module_param={item.id}
          module="project"
          filters={[
            {
              name: "project",
              value: item.id,
            },
          ]}
        />
      ),
    },

    {
      name: "Timeregistrering",
      icon: ClockIcon,
      active: projectPermissions.canTimeRegister,
      component: (
        <TimeRegistrationTab
          project={item}
          canManageProjectTime={
            projectPermissions.canEdit || projectPermissions.isAdmin
          }
          showHourlyCosts={projectPermissions.canSeeProjectHourlyCosts}
          restrictHoursToCurrentUserId={
            projectPermissions.isExecutorParticipant
              ? currentUserId ?? null
              : null
          }
        />
      ),
    },

    {
      name: "Vareliste",
      icon: ClipboardDocumentListIcon,
      active: projectPermissions.canReadItemLists,
      component: <ItemListTab project={item} />,
    },

    {
      name: "Fakturaer",
      icon: DocumentTextIcon,
      active: canProjectAccounting && projectPermissions.canRead,
      component: (
        <InvoicesTab key={item.id} project={item} canEdit={canEditAccountingUi} />
      ),
    },

    {
      name: "Leverandørfakturaer",
      icon: TruckIcon,
      active: canProjectAccounting && projectPermissions.canRead,
      component: (
        <SupplierInvoicesTab project={item} canEdit={canEditAccountingUi} />
      ),
    },

    {
      name: "Faktureringsplan",
      icon: ChartBarIcon,
      active: canProjectAccounting && projectPermissions.canRead,
      component: (
        <BillingPlanTab project={item} canEdit={canEditAccountingUi} />
      ),
    },

    {
      name: "Innstillinger",
      icon: CogIcon,
      active: projectPermissions.isAdmin,
      component: <ProjectSettingsTab project={item} onRefresh={handleOnRefresh} />,
    },

    {
      name: "Summering",
      icon: ChartBarIcon,
      active: canProjectAccounting && projectPermissions.canRead,
      component: <ProjectSummaryTab project={item} />,
    },

    {
      name: "Website",
      icon: GlobeAltIcon,
      active:
        websiteModuleActive &&
        projectPermissions.canRead &&
        !projectPermissions.isExecutorParticipant,
      component: <WebsiteTab project={item} onRefresh={handleOnRefresh} />,
    },
  ];

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedTab, setSelectedTab] = useState(
    parseInt(searchParams.get("tab"), 10) || 0
  );

  const tabSearchParam = searchParams.get("tab");
  useEffect(() => {
    if (tabSearchParam == null || tabSearchParam === "") return;
    const t = parseInt(tabSearchParam, 10);
    if (Number.isNaN(t)) return;
    setSelectedTab((prev) => (prev !== t ? t : prev));
  }, [tabSearchParam]);

  useEffect(() => {
    updateUrlWithException(
      // These will be updated
      {
        tab: selectedTab,
      }
    );
  }, [selectedTab]);


  const renderContent = () => {
    if (isLoading) {
      return <ResourceLoading />;
    }
    if (error && errors) {
      return <ResourceError errors={errors} />;
    }

    return (
      <>
        <DashboardHeader
          subtitle=""
          actions={headerActions}
          title={project?.displayTitle}
          meta={headerMeta}
          //   mainButton={{
          //     label: "New",
          //     action: () => setCaseDialogOpen(true),
          //     path: null,
          //     color: "",
          //     icon: PlusIcon,
          //   }}
        >
          <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
              <Grid container spacing={3}>
                <Grid
                  container
                  item
                  lg={12}
                  spacing={3}
                  sx={{ height: "fit-content", order: { md: 2, xs: 1 } }}
                >
                  <Grid item xs={12}>
                    <PMTabs
                      tabs={tabs}
                      selectedTab={selectedTab}
                      setSelectedTab={setSelectedTab}
                      maxVisibleTabs={6}
                    />
                  </Grid>
                </Grid>

                <Grid
                  container
                  item
                  lg={selectedTab === 0 ? 8 : 12}
                  spacing={3}
                  sx={{ height: "fit-content", order: { md: 2, xs: 1 } }}
                >
                  <Grid item xs={12}>
                    <div className="">{tabs[selectedTab].component}</div>
                  </Grid>
                </Grid>

                {selectedTab === 0 && (
                  <Grid
                    container
                    item
                    lg={4}
                    spacing={3}
                    sx={{ height: "fit-content", order: { md: 2, xs: 1 } }}
                  >
                    {projectPermissions.canEdit ? (
                    <Grid item xs={12}>
                      <DocumentUploadSectionV6
                        module="project"
                        moduleParam={item?.id}
                        selectedFolder={selectedFolder}
                        onFolderChange={handleFolderChange}
                        multiple={true}
                        project={item}
                      />
                    </Grid>
                    ) : null}
                    <Grid item xs={12}>
                      <PMLinkCard actions={rightSideActions} />
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </div>
          </div>
        </DashboardHeader>

        <ServiceReportEditModal
          customer={item?.customer}
          project={item}
          contact={item?.contact}
          delivery_address={item?.delivery_address}
          // fields={caseItemFields}
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
          selected={null}
          onRefresh={() => null}
        />

        <LabelPrintDialog
          open={labelPrintDialogOpen}
          isLoading={isLoading}
          onClose={() => setLabelPrintDialogOpen(false)}
          title={project?.project_number}
          subtitle={project?.name}
          description={project?.description}
          qr_code={`https://promang.no/dashboard/projects/${project?.id}`}
          // document_id={project?.main_picture?.id}
        />

        <FolderCreateDialog
          open={folderCreateDialogOpen}
          onClose={() => setFolderCreateDialogOpen(false)}
          project={item}
          onFolderCreated={handleFolderCreated}
        />

        <ChecklistTypeSelector
          open={checklistTypeSelectorOpen}
          onClose={() => setChecklistTypeSelectorOpen(false)}
          projectId={item?.id}
          project={item}
        />
      </>
    );
  };

  return <>{renderContent()}</>;
};
